import { ReactiveValue } from '@/utils/reactive-value/reactive-value';

import {
  type AiSuggestion,
  type APITeoMarker,
  type WordListData,
  type WordListEntry,
  type WordListExpression,
} from '../../TeoApi';
import { TeoEventTarget } from '../../utility/TeoEventTarget';
import type { TeoText } from '../TeoText';
import type { TeoView } from '../TextUi';
import { BrandFitHandler } from './BrandFitHandler';
import { DictionaryHandler } from './DictionaryHandler';
import { WhiteListHandler } from './WhiteListHandler';
import type { WordListSubHandler } from './WordListSubHandler';

export type MarkerDetails = { markerId: string; marker: APITeoMarker };
export type WordListHandlerEvents = {
  entryMarkerClicked: (relation: MarkerDetails) => void;
};

export type UiWordListSuggestion = {
  key: string;
  suggestion: string;
  variant: string;
  useAi: boolean;
};

export type UiWordListEntry = {
  key: string;
  wordListKey: string;
  cardLabel: string;
  contentText?: string;
  improvementsLabel?: string;
  suggestions: UiWordListSuggestion[];
  aiSuggestions?: AiSuggestion[];
  variant: string;
  variantLabel: string;
  checkedText?: string;
  isChecked: ReactiveValue<boolean>;
  onCheckedClick?: (entryKey: string, checked: boolean) => void;
  infoTextLabel?: string;
  infoText?: string;
  aiFixText?: string;
  onInfoClick?: () => void;
  aiLoading: ReactiveValue<boolean>;
};

type RelationType = {
  variant: string;
  cardLabel: string;
  improvementsLabel?: string;
  markerPriorities: number[];
};

const relationTypes: Record<number, RelationType> = {
  1: {
    variant: 'info',
    cardLabel: 'Begriff',
    improvementsLabel: 'Synonyme:',
    markerPriorities: [0, 1, 2],
  },
  2: {
    variant: 'phrases',
    cardLabel: 'Formulierung',
    improvementsLabel: 'Synonyme:',
    markerPriorities: [0],
  },
  3: {
    variant: 'wording',
    cardLabel: 'Schreibweise',
    improvementsLabel: 'Ersetze mit:',
    markerPriorities: [0],
  },
  //4: white list
  5: {
    variant: 'recommendation',
    cardLabel: 'Überprüfe',
    markerPriorities: [0],
  },
};

export class WordListHandler {
  public events = new TeoEventTarget<WordListHandlerEvents>();
  public brandFit: BrandFitHandler;
  public dictionary: DictionaryHandler;
  public whiteList: WhiteListHandler;

  public currentList: WordListSubHandler | null = null;

  // private lastScanResult: WordListScanResult | null = null;
  public ignoredEntries: {
    word_list_id: number;
    entry_id: number;
  }[] = [];
  // private markerClickRemover: (() => void) | null = null;
  constructor(private text: TeoText) {
    this.brandFit = new BrandFitHandler(this, text);
    this.dictionary = new DictionaryHandler(this, text);
    this.whiteList = new WhiteListHandler(text);
    this.text.markers.events.addEventListener(
      'markerClicked',
      ((detail: { markerId: string; marker: APITeoMarker }) => {
        this.handleMarkerClick(detail);
      }).bind(this),
    );
    this.text.store.addEventListener('view', (viewName: TeoView) => {
      if (viewName === 'dictionary') {
        this.currentList = this.dictionary;
      } else if (viewName === 'brand-scanner') {
        this.currentList = this.brandFit;
      } else {
        this.currentList = null;
      }
    });
  }

  handleMarkerClick(detail: MarkerDetails): void {
    if (detail.marker.groupName !== 'brand-scanner' && detail.marker.groupName !== 'dictionary') {
      return;
    }
    this.events.triggerEvent('entryMarkerClicked', detail);
  }

  getWordListVariant(wordList: WordListData): string {
    const type = wordList.relation_type;
    if (relationTypes[type]?.variant) {
      return relationTypes[type].variant;
    }
    return 'phrases';
  }

  getWordListEntryLabel(wordList: WordListData): string {
    const type = wordList.relation_type;
    if (relationTypes[type]?.cardLabel) {
      return relationTypes[type].cardLabel;
    }
    return 'TODO';
  }

  wordListUsesAiOnSuggestions(wordList: WordListData): boolean {
    return wordList.can_use_ai_suggestions ?? false;
  }

  getWordListEntryImprovementsLabel(wordList: WordListData): string | undefined {
    return relationTypes[wordList.relation_type]?.improvementsLabel;
  }

  createSuggestions(entry: WordListEntry, useAi: boolean): UiWordListSuggestion[] {
    const expressions: WordListExpression[] = entry.expressions;
    const suggestions = expressions
      .filter((e) => e.priority > 0)
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .map((e) => ({
        key: e.id.toString(),
        suggestion: e.full_expression,
        variant: 'suggestion',
        useAi,
      }));
    if (suggestions.length === 1 && suggestions[0]!.key == entry.found_by.toString()) {
      // If the only suggestion is the found_by expression, we don't need to show it.
      return [];
    }
    return suggestions;
  }

  replaceInText(relation: string, suggestion: UiWordListSuggestion): void {
    this.text.markers.replaceMarkerContent(relation, suggestion.suggestion);
  }

  createUiWordListEntry(
    entry: WordListEntry,
    wordList: WordListData,
    relationOffset: number,
  ): UiWordListEntry {
    const variant = this.getWordListVariant(wordList);
    const improvementsLabel = this.getWordListEntryImprovementsLabel(wordList);
    return {
      key: entry.current_relation.toString() + relationOffset.toString(),
      wordListKey: wordList.id.toString(),
      cardLabel: this.getWordListEntryLabel(wordList),
      improvementsLabel,
      contentText: !improvementsLabel ? entry.description : undefined,
      suggestions: this.createSuggestions(entry, this.wordListUsesAiOnSuggestions(wordList)),
      variant,
      variantLabel: entry.found_string || entry.title || '-',
      checkedText: variant == 'recommendation' ? 'Geprüft / OK' : undefined,
      // markerRelation: entry.current_relation,
      isChecked: new ReactiveValue<boolean>(false),
      onCheckedClick: (entryKey: string, checked: boolean) => {
        this.checkIgnoreEntry(wordList.id, entry.id, checked);
      },
      infoTextLabel: variant !== 'recommendation' && entry.description ? 'Erklärung' : undefined,
      infoText: entry.description,
      aiFixText: entry.ai_fix_text,
      onInfoClick:
        entry.description ?
          () => {
            this.openDescription(entry);
          }
        : undefined,
      aiLoading: new ReactiveValue<boolean>(false),
    };
  }

  private openDescription(entry: WordListEntry): void {
    this.text.core.notifications.add({
      mode: 'info',
      title: 'Erklärung: ' + entry.title,
      message: entry.description,
      buttons: [
        {
          label: 'Schließen',
        },
      ],
    });
  }

  checkIgnoreEntry(wordListId: number, entryId: number, checked: boolean): void {
    const isChecked = this.ignoredEntries.some(
      (e) => e.word_list_id === wordListId && e.entry_id === entryId,
    );
    if (checked && !isChecked) {
      this.ignoredEntries.push({ word_list_id: wordListId, entry_id: entryId });
    } else if (!checked && isChecked) {
      this.ignoredEntries = this.ignoredEntries.filter(
        (e) => !(e.word_list_id === wordListId && e.entry_id === entryId),
      );
    }
    this.text.touch();
  }

  getWordListMarkerNames(wordList: WordListData): string[] {
    const names: string[] = [];
    let markerPriorities: number[] = [0, 1, 2];
    if (relationTypes[wordList.relation_type]?.markerPriorities) {
      markerPriorities = relationTypes[wordList.relation_type]!.markerPriorities;
    }
    markerPriorities.forEach((priority) => {
      names.push(`${wordList.name}-prio-${priority}`);
    });
    if (wordList.rules && wordList.rules.length > 0) {
      wordList.rules.forEach((rule) => {
        names.push(`rule_${rule.ai_tag}`);
      });
    }
    return names;
  }

  getWordListFoundEntries(wordList: WordListData): WordListEntry[] {
    const markerPriorities = relationTypes[wordList.relation_type]?.markerPriorities ?? [0, 1, 2];
    return wordList.current_text_entries.filter((entry) =>
      markerPriorities.includes(
        entry.expressions.find((expression) => expression.id == entry.found_by)?.priority ?? -1,
      ),
    );
  }

  mark(lists: WordListData[], clickable = true): void {
    const markerNames: string[] = [];
    lists.forEach((wordList) => {
      const names = this.getWordListMarkerNames(wordList);
      markerNames.push(...names);
    });
    this.setMarkers(markerNames, clickable);
  }

  setMarkers(markerNames: string[], clickable = true): void {
    if (clickable) {
      this.text.markers.setMarkers([], markerNames);
    } else {
      this.text.markers.setMarkers(markerNames);
    }
  }

  // private triggerScanPending(wordListCategory: WordListCategory, pending: boolean) {
  //   this.events.triggerEvent('scanPending', pending);
  //   if (wordListCategory === 'brand-scanner' || wordListCategory === 'all') {
  //     this.events.triggerEvent('brandFitScanPending', pending);
  //   }
  //   if (wordListCategory === 'dictionary' || wordListCategory === 'all') {
  //     this.events.triggerEvent('dictionaryScanPending', pending);
  //   }
  // }

  // async scan(wordListCategory: WordListCategory = 'all') {
  //   const textArray = this.text.getTextArray?.();
  //   if (!textArray) {
  //     console.error('No text array available for scanning.');
  //     return;
  //   }
  //   this.triggerScanPending(wordListCategory, true);
  //   const request: WordListScanRequest = {
  //     text: textArray,
  //     ignored_entries: this.ignoredEntries,
  //     word_list_category: wordListCategory,
  //   };

  //   if (this.text.core.dynamicConfig.options.useExpParser) {
  //     request.use_exp_parser = true;
  //   }

  //   const response = await this.text.core.api.getWordListScan(request);
  //   if (ApiError.matches(response)) {
  //     console.error('Error in word list scan:', response);
  //     this.triggerScanPending(wordListCategory, false);
  //     return;
  //   }
  //   // this.lastScanResult = response;
  //   this.markers = [];
  //   console.log('Word list scan result:', response);
  //   response.markers.forEach((marker) => {
  //     this.markers.push({
  //       ...marker,
  //       groupName: 'wordlist',
  //       color: marker.color || this.getMarkerColor(marker.marker),
  //     });
  //   });
  //   this.events.triggerEvent('gotScanResult', response);
  //   this.triggerScanPending(wordListCategory, false);
  // }
}
